import React, { useState } from 'react';
import CreateModalTemplate from '@component/gridgio/modal/dialog-modal/create-modal-template';
import { useTypedDispatch } from '@hook/common/useStore';
import { actions as modalActions } from '@store/slice/gridgio/modal-slice';
import type { ICreateWorkModalData } from '@datatype/gridgio/type';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Gap from '@component/common/gap';
import PlanThumbnail from '@component/gridgio/project/plan/plan-thumbnail';
import TextBlock from '@component/common/block/text-block';
import TextInput from '@component/common/input/text-input';
import { createProjectPath } from '@constant/gridgio/route-path';
import { useCreateWorkMutation } from '@hook/gridgio/useApi';
import * as color from '@constant/color';
import * as size from '@constant/size';

interface IProps {
  data: ICreateWorkModalData['data'];
}

const PlanThumbnailContainer = styled.div`
  width: 75px;
  height: 50px;
  overflow: hidden;
  border-radius: ${size.BORDER_RADIUS_1};
`;

const SourceItemContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: ${size.MARGIN_1};
  padding: ${size.MARGIN_1};
  border: ${size.BORDER_WIDTH_1} solid ${color.GRAY_2};
  border-radius: ${size.BORDER_RADIUS_1};
`;
const SourceInfoContainer = styled.div`
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 5px;
`;

const LABEL_DESIGN = {
  box: { width: '100%', alignX: 'left' },
  text: { size: 'medium', color: color.BLACK_2, bold: true },
} as const;
const INPUT_DESIGN = {
  box: {
    width: '100%',
    height: '42px',
    padding: `0 ${size.MARGIN_1}`,
    border: true,
    borderWidth: size.BORDER_WIDTH_1,
    borderColor: color.GRAY_2,
    borderRadius: size.BORDER_RADIUS_1,
    alignX: 'left',
  },
  text: { size: 'medium', color: color.BLACK_2 },
} as const;

const CreateWorkModal: React.FC<IProps> = ({ data }) => {
  const dispatch = useTypedDispatch();
  const { projectKey, plan } = data;
  const source = plan.source;
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const workMutation = useCreateWorkMutation();
  const isPending = workMutation.isPending;

  const handleClose = () => {
    if (!isPending) dispatch(modalActions.hideModal());
  };

  const handleCreate = async () => {
    const trimmedName = name.trim();
    if (!trimmedName || !plan.key || isPending) return;

    setErrorMessage('');
    try {
      const workResponse = await workMutation.mutateAsync({
        projectKey,
        planKey: plan.key,
        name: trimmedName,
        configuration: {},
      });
      const workKey = workResponse.data.key;

      dispatch(modalActions.hideModal());
      navigate(createProjectPath('work', workKey));
    } catch {
      setErrorMessage('작업을 만들지 못했습니다. 다시 시도해주세요.');
    }
  };

  return (
    <CreateModalTemplate
      title='새 작업'
      confirmText='작업 시작'
      errorMessage={errorMessage}
      isPending={isPending}
      confirmDisabled={!name.trim() || !plan.key}
      handleCancel={handleClose}
      handleConfirm={() => void handleCreate()}
      handleSubmit={() => void handleCreate()}
      input={
        <>
          <TextBlock text='작업 이름' design={LABEL_DESIGN} />
          <Gap height={size.MARGIN_1} />
          <TextInput
            name='work-name'
            value={name}
            placeholder='작업 이름을 입력해주세요'
            design={INPUT_DESIGN}
            onChange={(event) => setName(event.target.value)}
          />
          <Gap height={size.MARGIN_2} />
          <TextBlock text='선택한 도면' design={LABEL_DESIGN} />
          <Gap height={size.MARGIN_1} />
          <SourceItemContainer>
            <PlanThumbnailContainer>
              <PlanThumbnail source={source} />
            </PlanThumbnailContainer>
            <SourceInfoContainer>
              <TextBlock
                text={plan.name ?? ''}
                design={{
                  box: { width: '100%', alignX: 'left' },
                  text: { size: 'medium', color: color.BLACK_2, bold: true, oneline: true },
                }}
              />
              <TextBlock
                text={source.filename}
                design={{
                  box: { width: '100%', alignX: 'left' },
                  text: { size: 'small', color: color.GRAY_1, oneline: true },
                }}
              />
            </SourceInfoContainer>
          </SourceItemContainer>
        </>
      }
    />
  );
};

export default CreateWorkModal;
